import { Trans } from "@lingui/react/macro";
import Select from "@medoma/components/Select/Select";
import { useInvoicingCodesFromPatientId } from "@/api/Invoicing";
import { invoicingCodeTypeOptionsSchema } from "@medoma/models/invoicing";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Heading } from "@medoma/components/Heading/Heading";
import { useFormContext, type FieldValues } from "react-hook-form";
import { useLingui } from "@lingui/react/macro";
import { errorMessageAt, withPrefix } from "@/forms/fieldPath";

export type IInvoicingCodesPickerFields = {
  procedureCodes: string[];
  productCodes: string[];
};

export const InvoicingPicker = ({
  patientId,
  namePrefix,
}: {
  patientId: string;
  namePrefix?: string;
}) => {
  const { t } = useLingui();
  const {
    formState: { errors },
    register,
  } = useFormContext<FieldValues>();

  const procedureCodesPath = withPrefix(namePrefix, "procedureCodes");
  const productCodesPath = withPrefix(namePrefix, "productCodes");
  const procedureCodesError = errorMessageAt(errors, procedureCodesPath);
  const productCodesError = errorMessageAt(errors, productCodesPath);

  const {
    data: invoicingCodes,
    isPending,
    isError,
  } = useInvoicingCodesFromPatientId({ patientId, codeType: null });

  if (isPending) {
    return (
      <div>
        <Heading level="h3" weight="medium" margin="bottom">
          <Trans>Fakturering</Trans>
        </Heading>
        <Loading message={t`Hämtar faktureringskoder`} />
      </div>
    );
  }

  if (isError) {
    return (
      <div>
        <Heading level="h3" weight="medium" margin="bottom">
          <Trans>Fakturering</Trans>
        </Heading>
        <ErrorMessage message={t`Kunde inte hämta faktureringskoder`} />
      </div>
    );
  }

  const procedureCodes = invoicingCodes.filter(
    (code) => code.type === invoicingCodeTypeOptionsSchema.enum.procedureCode,
  );

  const productCodes = invoicingCodes.filter(
    (code) => code.type === invoicingCodeTypeOptionsSchema.enum.productCode,
  );

  const hasProcedureCodes = procedureCodes.length > 0;
  const hasProductCodes = productCodes.length > 0;

  return (
    <>
      {hasProcedureCodes ? (
        <Select
          label={t`Åtgärd (KVÅ)`}
          description={t`Välj flera åtgärder genom att hålla ner Ctrl-tangenten (⌘ på Mac)`}
          multiple
          {...register(procedureCodesPath)}
          errorMessage={procedureCodesError}
        >
          {procedureCodes.map((procedureCode) => (
            <option key={procedureCode.id} value={procedureCode.id}>
              {`${procedureCode.code}, ${procedureCode.name}`}
            </option>
          ))}
        </Select>
      ) : null}
      {hasProductCodes ? (
        <Select
          label={t`Produkt`}
          description={t`Välj flera produkter genom att hålla ner Ctrl-tangenten (⌘ på Mac)`}
          multiple
          {...register(productCodesPath)}
          errorMessage={productCodesError}
        >
          {productCodes.map((productCode) => (
            <option key={productCode.id} value={productCode.id}>
              {`${productCode.code}, ${productCode.name}`}
            </option>
          ))}
        </Select>
      ) : null}
    </>
  );
};
